import { Store } from '../store'; // eslint-disable-line no-unused-vars
import { makeAutoObservable } from 'mobx';

class ProfilerAccessStore {
  /**
   * @param {Store} root
   */
  constructor(root) {
    makeAutoObservable(this);
    this.root = root;
  }

  get userInfo() {
    return this.root.authStore.userInfo;
  }

  get isProfilerViewer() {
    return !!this.userInfo?.isCognitoProfilerViewer;
  }

  get isProfilerDemoLite() {
    return !!this.userInfo?.isCognitoProfilerDemoLite;
  }

  get isProfilerDemoFull() {
    return !!this.userInfo?.isCognitoProfilerDemoFull;
  }

  get isProfilerDemo() {
    return this.isProfilerDemoLite || this.isProfilerDemoFull;
  }

  get isProfilerLite() {
    return !!this.userInfo?.isCognitoProfilerLite;
  }

  get isProfilerFull() {
    return !!this.userInfo?.isCognitoProfilerFull;
  }

  // Lite family and NOT also Full — mirrors BE `permissions.isProfilerLiteOnly`
  // (TD-2623). Used to hide EBIT / EBIT-margin from Lite across search + comparables
  // where there is no `project` context to defer to the extended getter.
  get isProfilerLiteOnly() {
    return (
      (this.isProfilerLite || this.isProfilerDemoLite) &&
      !(this.isProfilerFull || this.isProfilerDemoFull)
    );
  }

  // Per-company gating (enrichment_credit_limit !== null) is enforced by BE,
  // which omits the `enrichment` block from /subscription-status when NULL.
  // UI components combine this getter with `useSubscriptionStatus().enrichment != null`.
  get isCognitoContactEnrichment() {
    return !!this.userInfo?.isCognitoContactEnrichment;
  }

  get hasAnyProfilerAccess() {
    return (
      this.isProfilerViewer || this.isProfilerDemo || this.isProfilerLite || this.isProfilerFull
    );
  }

  get canUseProfilerSearch() {
    return this.isProfilerLite || this.isProfilerDemo || this.isProfilerFull;
  }

  canViewProfilerDetailsExtendedData = (project) => {
    return (
      this.isProfilerFull ||
      this.isProfilerDemoFull ||
      this.root.authStore.companyUsers.findIndex(
        (u) => u.id === project?.ownerId && u.isCognitoProfilerFull
      ) !== -1
    );
  };

  // Lite-family access to the company-details content (contacts, shareholders,
  // and the revenue/employees overview the BE now exposes — TD-2498/TD-2499).
  // Lite / Demo-Lite short-circuit before any `project` deref; every other role
  // defers to the Full-only extended getter. Demo-Lite mirrors Lite just as
  // Demo-Full mirrors Full in canViewProfilerDetailsExtendedData.
  canViewProfilerDetails = (project) => {
    return (
      this.isProfilerLite ||
      this.isProfilerDemoLite ||
      this.canViewProfilerDetailsExtendedData(project)
    );
  };

  get canExportProfilerOnepager() {
    return this.isProfilerLite || this.isProfilerFull;
  }

  // Bulk contacts XLSX ("Download contact list as XLSX") on saved projects and
  // search results. Contacts became Lite content with TD-2498/TD-2499, so this is
  // open to the whole paid family. TD-2652 briefly included the demo tiers as well;
  // TD-2646 withdrew that — demo accounts are entitled to no export at all, and this
  // was the last one they could still run. The BE half is TD-2879.
  get canDownloadProfilerContactsList() {
    return this.isProfilerLite || this.isProfilerFull;
  }

  get canExportProfilerFinancials() {
    return this.isProfilerFull;
  }

  // TD-2635: the detailed profile is no longer Full-only. TD-2634 shapes the deck
  // server-side by role — the Lite family receives a variant without the two
  // detailed financials slides — so this gate only decides whether the request
  // goes out, never what ends up in the file. Demo roles stay excluded to match
  // canExportProfilerOnepager: the FE has never granted demo accounts an export,
  // and that sibling getter also feeds the bulk onepager-list surfaces.
  get canExportProfilerFullProfiler() {
    return this.isProfilerLite || this.isProfilerFull;
  }

  get canExportProfilerComparableCompanies() {
    return this.isProfilerLite || this.isProfilerFull;
  }

  get canExportCompanyContacts() {
    return this.isProfilerLite || this.isProfilerFull;
  }

  get canViewProfilerWatchlist() {
    return this.isProfilerDemo || this.isProfilerLite || this.isProfilerFull;
  }

  // The `profilerexportdisabled` right: a per-user withdrawal that stacks on any
  // profiler role. TD-2648 moved it off the download trigger and into the per-export
  // disabled state — the menu now always opens and every entry inside is disabled
  // instead, so the user can still see what the product produces. Read it with
  // `exportBlockedNode` (components/common/exportAccessTooltips) to get the matching
  // message, which must stay distinct from the not-entitled one.
  get isProfilerExportDisabled() {
    return !!this.userInfo?.isCognitoProfilerExportDisabled;
  }

  get allowedRequestFeedbackUsersList() {
    return this.root.authStore.companyUsers.filter(
      (u) =>
        u.id !== this.root.authStore.currentUserId &&
        (!!u.isCognitoProfilerViewer ||
          !!u.isCognitoProfilerDemoLite ||
          !!u.isCognitoProfilerDemoFull ||
          !!u.isCognitoProfilerLite ||
          !!u.isCognitoProfilerFull)
    );
  }

  get demoSearchCount() {
    return this.root.authStore.userInfo?.profilerSearchExecutedCount || 0;
  }

  get demoSearchMaxCount() {
    return this.root.authStore.userInfo?.profilerSearchMaxCount || 10;
  }

  get demoSearchRemainingCount() {
    return this.demoSearchMaxCount - this.demoSearchCount;
  }

  get demoSearchExceededLimit() {
    return this.demoSearchRemainingCount <= 0;
  }
}

export default ProfilerAccessStore;
